/**
 * The writer's own side of MetaTrack — what a writer login sees.
 * Dashboard, My songs, Royalties and the search box. Payouts lives in
 * Payouts.jsx (MyPayouts). Everything here comes from the /api/my/* routes,
 * which only ever return the signed-in writer's own songs and money.
 */
import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, Check, ChevronDown, ChevronRight, Disc3, ListMusic, Search, Wallet } from "lucide-react";
import Layout from "@/components/Layout";
import { EmptyState, LoadingPanel, StatCard } from "@/components/Bits";
import { Pill, FilterSelect } from "@/components/Music";
import { Bars, Money, money, monthLabel, serviceName } from "@/components/Royalty";
import { StatusPill } from "@/components/Payout";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Sheet, SheetContent, SheetDescription, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { api, apiError, formatDate } from "@/lib/api";
import { useRemembered } from "@/lib/useRemembered";
import { cn } from "@/lib/utils";

const SONG_TONE = { Registered: "good", Submitted: "brand", Pending: "warn", Rejected: "bad", "Needs update": "warn", "Action needed": "act" };
// statuses the writer may need to act on
const ATTENTION = ["Action needed", "Needs update", "Rejected"];

export function SongStatus({ status }) {
  return <Pill tone={SONG_TONE[status] || "muted"}>{status}</Pill>;
}

function useLoad(name, url, params) {
  const [data, setData] = useRemembered(name);
  const key = JSON.stringify(params || {});
  useEffect(() => {
    let live = true;
    api
      .get(url, { params })
      .then(({ data: d }) => live && setData(d, key === "{}"))
      .catch((e) => toast.error(apiError(e)));
    return () => {
      live = false;
    };
  }, [url, key]); // eslint-disable-line react-hooks/exhaustive-deps
  return data;
}

// ------------------------------------------------------------------ dashboard
export function WriterDashboard() {
  const d = useLoad("my-dashboard", "/my/dashboard");
  const nav = useNavigate();
  if (!d)
    return (
      <Layout title="Dashboard" subtitle="Loading…">
        <div className="min-h-[70vh]">
          <LoadingPanel rows={6} />
        </div>
      </Layout>
    );
  const first = (d.name || "").split(" ")[0];
  const sc = d.status_counts;
  const att = ATTENTION.reduce((a, k) => a + (sc[k] || 0), 0);
  const attLine = [
    sc["Action needed"] ? `${sc["Action needed"]} need${sc["Action needed"] === 1 ? "s" : ""} something from you` : "",
    sc.Rejected ? `${sc.Rejected} rejected` : "",
    sc["Needs update"] ? `${sc["Needs update"]} need an update` : "",
  ].filter(Boolean).join(" · ");
  const next = d.next_date ? formatDate(d.next_date) : `when ${d.publisher} runs payouts`;
  const balanceLine =
    d.request
      ? `Withdrawal requested ${formatDate(d.request.requested_at)}`
      : d.status === "ready"
      ? `Ready to withdraw · next payout ${next}`
      : d.status === "below"
      ? `Paid once you reach ${money(d.min, { places: 2 })}`
      : d.status === "hold"
      ? "Payouts on hold"
      : "Nothing owed right now";
  return (
    <Layout title={`Hi ${first}`} subtitle={`Your songs and royalties with ${d.publisher}`}>
      <div className="grid grid-cols-2 xl:grid-cols-4 gap-4 mb-4">
        <StatCard label="Your balance" value={money(d.balance)} tone={d.status === "ready" ? "good" : "default"} sub={balanceLine} icon={Wallet} onClick={() => nav("/payouts")} />
        <StatCard label={d.label} value={money(d.earned)} sub="From every statement so far" icon={Disc3} onClick={() => nav("/royalties")} />
        <StatCard label="Your songs" value={d.songs} sub={`${sc.Registered} registered`} icon={ListMusic} onClick={() => nav("/my-songs")} />
        <StatCard
            label="Need attention"
            value={att}
            tone={att ? "warn" : "default"}
            sub={att ? attLine : "Nothing waiting on anyone"}
            icon={ArrowRight}
            onClick={() => nav("/my-songs?status=attention")}
          />
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <section className="panel p-5">
          <div className="flex items-center mb-3">
            <span className="eyebrow">{d.label} by month</span>
            <Link to="/royalties" className="ml-auto text-[12.5px] font-semibold text-brand">
              Royalties
            </Link>
          </div>
          {d.by_period.length ? (
            <Bars labelWidth={90} items={[...d.by_period].reverse().map((p) => ({ key: p.period, label: monthLabel(p.period), amount: p.amount }))} />
          ) : (
            <p className="text-[13px] text-muted2">No earnings yet. They appear here once a statement with your songs comes in.</p>
          )}
        </section>
        <section className="panel p-5">
          <div className="eyebrow mb-3">Top songs</div>
          {d.top_songs.length ? (
            d.top_songs.map((s, i) => (
              <Link key={s.track_id} to={`/my-songs?open=${s.track_id}`} className="flex items-center gap-3 py-2 border-t border-line first:border-t-0 hover:text-brand">
                <span className="font-mono text-[12px] text-muted2 w-4">{i + 1}</span>
                <span className="flex-1 text-[13.5px] font-semibold truncate">{s.title}</span>
                <Money value={s.amount} className="text-[12.5px]" />
              </Link>
            ))
          ) : (
            <p className="text-[13px] text-muted2">Nothing earned yet.</p>
          )}
        </section>
        <section className="panel p-5">
          <div className="eyebrow mb-3">Songs that need attention</div>
          {d.attention.length ? (
            d.attention.map((s) => (
              <Link key={s.track_id} to={`/my-songs?open=${s.track_id}`} className="block py-2.5 border-t border-line first:border-t-0 hover:text-brand">
                <div className="flex items-center justify-between gap-3">
                  <span className="text-[13.5px] font-semibold truncate">{s.title}</span>
                  <SongStatus status={s.status} />
                </div>
                {s.status_note && <div className="text-[12.5px] text-muted2 mt-0.5">{s.status_note}</div>}
              </Link>
            ))
          ) : (
            <p className="text-[13px] text-muted2">All your songs are on track.</p>
          )}
        </section>
        <section className="panel p-5">
          <div className="eyebrow mb-3">Last payout</div>
          {d.last_payout ? (
            <div className="flex items-baseline justify-between gap-3">
              <span className="text-[13.5px]">{formatDate(d.last_payout.date)}</span>
              <span className="font-display text-[24px] font-semibold">{money(d.last_payout.amount, { places: 2 })}</span>
            </div>
          ) : (
            <p className="text-[13px] text-muted2">No payouts yet.</p>
          )}
          <Link to="/payouts" className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-brand mt-3">
            Go to Payouts <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </section>
      </div>
    </Layout>
  );
}

// ------------------------------------------------------------------ my songs
const STATUS_ORDER = ["Action needed", "Needs update", "Rejected", "Pending", "Submitted", "Registered"];
const STATUS_DOT = { Registered: "bg-stock-in", Submitted: "bg-brand", Pending: "bg-muted2/70", Rejected: "bg-stock-out", "Needs update": "bg-gold", "Action needed": "bg-orange-600 dark:bg-orange-400" };
// registered at some societies (not every one that applies) gets a half-filled dot
const HALF = { background: "linear-gradient(90deg, hsl(var(--stock-in)) 50%, transparent 50%)", border: "1.5px solid hsl(var(--stock-in))" };
const listNames = (xs) => (xs.length > 1 ? `${xs.slice(0, -1).join(", ")} and ${xs[xs.length - 1]}` : xs[0] || "");
const shortDate = (d) => (d ? new Date(`${d.slice(0, 10)}T12:00:00`).toLocaleDateString(undefined, { month: "short", year: "numeric" }) : "—");
const songArtist = (s) => (s.artist || "") + (s.featured ? ` ft. ${s.featured}` : "");

/** Highlight where the search matched. */
function Hl({ text, q }) {
  const t = String(text || "");
  const i = q ? t.toLowerCase().indexOf(q.toLowerCase()) : -1;
  if (i < 0) return t;
  return (
    <>
      {t.slice(0, i)}
      <mark className="bg-gold-light text-inherit rounded-[3px] px-px">{t.slice(i, i + q.length)}</mark>
      {t.slice(i + q.length)}
    </>
  );
}

/** A song's details, sliding up from the bottom on phones and in from the right on wider screens. */
function SongSheet({ s, onClose, publisher }) {
  const narrow = useNarrow();
  const colours = ["bg-brand", "bg-gold", "bg-sec-release", "bg-muted2"];
  const total = s ? s.writers.reduce((a, w) => a + (Number(w.share) || 0), 0) || 1 : 1;
  return (
    <Sheet open={!!s} onOpenChange={(v) => !v && onClose()}>
      <SheetContent
        side={narrow ? "bottom" : "right"}
        className={cn("p-0 flex flex-col", narrow ? "max-h-[84vh] rounded-t-2xl" : "w-full sm:max-w-md")}
        // focus the panel without letting the browser scroll the list behind it
        onOpenAutoFocus={(e) => {
          e.preventDefault();
          e.currentTarget?.focus?.({ preventScroll: true });
        }}
      >
        {s && (
          <>
            {narrow && <div className="mx-auto mt-2.5 h-1 w-10 rounded-full bg-line shrink-0" aria-hidden="true" />}
            <div className="px-5 pt-4 pb-4 border-b border-line pr-12">
              <SheetTitle className="font-display text-[22px] font-semibold leading-tight">{s.title}</SheetTitle>
              <SheetDescription className="text-[12.5px] text-muted2">{songArtist(s) || "—"}</SheetDescription>
              <div className="mt-3">
                <SongStatus status={s.status} />
                {s.status === "Action needed" ? (
                  <div className="mt-2.5 rounded-lg border border-orange-300/60 dark:border-orange-400/40 bg-orange-50 dark:bg-orange-950/40 px-3 py-2.5 text-[13px]">
                    <div className="font-semibold">{publisher || "Your publisher"} needs something from you</div>
                    {s.status_note && <div className="mt-1">{s.status_note}</div>}
                    <div className="text-[11.5px] text-muted2 mt-1.5">Get in touch with them to sort it out.</div>
                  </div>
                ) : s.status_note ? (
                  <div className={cn("mt-2.5 rounded-lg border px-3 py-2.5 text-[13px]", s.status === "Rejected" ? "border-stock-out/25 bg-rose-50 dark:bg-rose-950/40" : s.status === "Needs update" ? "border-gold/30 bg-gold-light" : "border-line bg-surface")}>
                    {s.status_note}
                  </div>
                ) : s.partial ? (
                  <p className="text-[13px] text-muted2 mt-1.5">
                    Registered at {listNames((s.societies || []).filter((x) => x.status === "registered").map((x) => x.name))}. We're working on the rest.
                  </p>
                ) : null}
              </div>
            </div>
            <div className="px-5 py-4 overflow-y-auto flex-1">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="eyebrow text-[10px]">Release date</div>
                  <div className="text-[13.5px] mt-1">{s.release_date ? formatDate(s.release_date) : "—"}</div>
                </div>
                <div>
                  <div className="eyebrow text-[10px]">ISRC</div>
                  <div className="text-[13.5px] mt-1 font-mono">{s.isrc || <span className="text-stock-out">Missing</span>}</div>
                </div>
              </div>
              {(s.societies || []).length > 0 && s.status !== "Pending" && (
                <>
                  <div className="eyebrow text-[10px] mt-5 mb-1">Where it's registered</div>
                  {s.societies.map((x) => (
                    <div key={x.key} className="py-2 border-t border-line first-of-type:border-t-0 text-[13px]">
                      <div className="flex items-center gap-2">
                        <span
                          className={cn(
                            "w-2 h-2 rounded-full shrink-0",
                            { registered: "bg-stock-in", submitted: "bg-brand", rejected: "bg-stock-out", being_fixed: "bg-gold", pending: "bg-muted2/70" }[x.status]
                          )}
                        />
                        <span>{x.name}</span>
                        <span
                          className={cn(
                            "ml-auto text-[12.5px]",
                            x.status === "registered" ? "text-stock-in font-semibold" : x.status === "being_fixed" ? "text-gold font-semibold" : x.status === "rejected" ? "text-stock-out font-semibold" : "text-muted2"
                          )}
                        >
                          {{ registered: "✓ Registered", submitted: "Submitted", rejected: "Rejected", being_fixed: "Being fixed", pending: "Pending" }[x.status]}
                        </span>
                      </div>
                      {x.status === "being_fixed" && x.note && <div className="text-[12px] text-muted2 mt-0.5 pl-4">{x.note}</div>}
                    </div>
                  ))}
                </>
              )}
              <div className="eyebrow text-[10px] mt-5 mb-2">Writers and splits</div>
              <div className="flex h-2.5 rounded-full overflow-hidden bg-surface mb-2">
                {s.writers.map((w, i) => (
                  <span key={i} className={cn("block h-full", w.me ? "bg-brand" : colours[(i % (colours.length - 1)) + 1], !w.me && "opacity-60")} style={{ width: `${((Number(w.share) || 0) / total) * 100}%` }} />
                ))}
              </div>
              {s.writers.map((w, i) => (
                <div key={i} className="flex justify-between gap-3 py-2 border-t border-line text-[13px]">
                  <span>
                    {w.name} {w.me && <Pill tone="brand">You</Pill>}
                  </span>
                  <span className="font-mono">{w.share != null ? `${Number(w.share)}%` : "—"}</span>
                </div>
              ))}
            </div>
            <div className="px-5 py-3 border-t border-line">
              <Button className="w-full bg-brand hover:bg-brand-hover" onClick={onClose}>
                Done
              </Button>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}

/** The status filter: MetaTrack's own small menu (not the phone's picker), the same on every device. */
function StatusMenu({ value, counts, total, onChange }) {
  const [open, setOpen] = useState(false);
  // "Needs attention" groups the problem statuses; it only shows when there's more than one kind,
  // otherwise it would just repeat that one status
  const kinds = ATTENTION.filter((k) => counts[k]);
  const attention = kinds.length > 1 || value === "attention" ? kinds.reduce((a, k) => a + counts[k], 0) : 0;
  const options = [
    { v: "", label: "All statuses", n: total, dot: "border-[1.5px] border-muted2 bg-transparent" },
    ...(attention ? [{ v: "attention", label: "Needs attention", n: attention, dot: "", style: { background: "conic-gradient(#ea580c 0 33%, hsl(var(--gold)) 0 66%, hsl(var(--stock-out)) 0)" } }] : []),
    ...STATUS_ORDER.filter((k) => counts[k] || value === k).map((k, i) => ({ v: k, label: k, n: counts[k], dot: STATUS_DOT[k], first: i === 0 })),
  ];
  const cur = options.find((o) => o.v === value) || options[0];
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={`Filter by status: ${cur.label}`}
          className={cn("inline-flex items-center gap-2 h-9 px-2.5 rounded-lg border bg-card text-[12.5px] font-semibold shrink-0 max-w-[170px] sm:max-w-none", open ? "border-brand-ring ring-2 ring-brand-ring/50" : "border-line hover:border-brand-ring")}
        >
          {value && <span className={cn("w-2 h-2 rounded-full shrink-0", cur.dot)} style={cur.style} />}
          <span className="truncate">{value ? `${cur.label} (${cur.n})` : cur.label}</span>
          <ChevronDown className="w-3.5 h-3.5 text-muted2 shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" sideOffset={6} className="w-60 p-1.5 rounded-[10px] border-line bg-card">
        <div role="listbox" aria-label="Status">
          {options.map((o) => (
            <div key={o.v || "all"}>
              {o.first && options[0].v === "" && <div className="h-px bg-line my-1 mx-1.5" />}
              <button
                type="button"
                role="option"
                aria-selected={o.v === value}
                onClick={() => {
                  onChange(o.v);
                  setOpen(false);
                }}
                className={cn("w-full flex items-center gap-2.5 px-2.5 py-2 rounded-md text-[13px] text-left", o.v === value ? "bg-brand-light font-semibold" : "hover:bg-surface")}
              >
                <span className="w-3.5 text-brand text-[12px]">{o.v === value ? <Check className="w-3.5 h-3.5" /> : null}</span>
                <span className={cn("w-2 h-2 rounded-full shrink-0", o.dot)} style={o.style} />
                <span className="flex-1 truncate">{o.label}</span>
                <span className="font-mono text-[11.5px] text-muted2">{o.n}</span>
              </button>
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export function MySongs() {
  const [params, setParams] = useSearchParams();
  const data = useLoad("my-songs", "/my/songs");
  const [q, setQ] = useState("");
  // the open song lives in state, not the address, so tapping a song never moves the list;
  // a link from the dashboard (?open=…) still opens it
  const [openId, setOpenId] = useState(() => params.get("open"));
  useEffect(() => {
    const fromLink = params.get("open");
    if (fromLink) setOpenId(fromLink);
  }, [params]);
  const statusParam = params.get("status") || "";
  const all = data?.songs || [];
  const counts = Object.fromEntries(STATUS_ORDER.map((k) => [k, all.filter((s) => s.status === k).length]));
  const wanted = statusParam === "attention" ? ATTENTION : statusParam ? [statusParam] : null;
  const n = q.trim().toLowerCase();
  const songs = all.filter(
    (s) => (!wanted || wanted.includes(s.status)) && (!n || `${s.title} ${s.artist} ${s.featured || ""} ${s.isrc}`.toLowerCase().includes(n))
  );
  const open = all.find((s) => s.track_id === openId);
  const setParam = (k, v) => {
    const next = new URLSearchParams(params);
    v ? next.set(k, v) : next.delete(k);
    setParams(next, { replace: true });
  };
  const closeSong = () => {
    setOpenId(null);
    if (params.get("open")) setParam("open", "");
  };
  const showing = [statusParam === "attention" ? "Needs attention" : statusParam, n ? `matching “${q.trim()}”` : ""].filter(Boolean).join(", ");
  // why a song is in the search results when the title doesn't say so
  const reason = (s) => {
    if (!n || s.title.toLowerCase().includes(n)) return null;
    if (songArtist(s).toLowerCase().includes(n)) return <Hl text={songArtist(s)} q={q.trim()} />;
    if ((s.isrc || "").toLowerCase().includes(n)) return <span className="font-mono"><Hl text={s.isrc} q={q.trim()} /></span>;
    return null;
  };
  return (
    <Layout title="My songs" subtitle={data ? `${all.length} song${all.length === 1 ? "" : "s"} you're credited on` : "Loading…"}>
      <div className="min-h-[70vh]">
        {!data ? (
          <LoadingPanel rows={6} />
        ) : !all.length ? (
          <div className="panel">
            <EmptyState icon={ListMusic} title="No songs yet" body="Songs appear here once your publisher credits you on them." />
          </div>
        ) : (
          <>
            <div className="flex items-center gap-2 mb-2">
              <label className="relative flex-1 min-w-0 sm:max-w-[300px]">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted2" />
                <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Title, artist or ISRC…" aria-label="Filter your songs" className="w-full h-9 pl-9 pr-3 rounded-lg border border-line bg-card text-[13px] outline-none focus:border-brand-ring" />
              </label>
              <StatusMenu value={statusParam} counts={counts} total={all.length} onChange={(v) => setParam("status", v)} />
            </div>
            <div className="text-[12px] text-muted2 mb-2 min-h-[18px]">
              {showing ? `${songs.length} song${songs.length === 1 ? "" : "s"} · ${showing}` : ""}
            </div>

            <div className="panel overflow-hidden">
              {/* phones: song, released and status, with no sideways scrolling */}
              <div className="sm:hidden">
                <div className="grid grid-cols-[minmax(0,1fr)_64px_104px] gap-2.5 px-4 py-2 bg-surface/70 border-b border-line">
                  {["Song", "Released", "Status"].map((h) => (
                    <span key={h} className="t-head">
                      {h}
                    </span>
                  ))}
                </div>
                {songs.map((s) => {
                  const why = reason(s);
                  return (
                    <button
                      key={s.track_id}
                      type="button"
                      onClick={() => setOpenId(s.track_id)}
                      className={cn("w-full grid grid-cols-[minmax(0,1fr)_64px_104px] gap-2.5 items-center px-4 py-3 text-left border-b border-line last:border-b-0", s.track_id === openId ? "bg-brand-light/60" : "active:bg-surface/60")}
                    >
                      <span className="min-w-0">
                        <span className="block text-[13.5px] font-semibold truncate">{n && !why ? <Hl text={s.title} q={q.trim()} /> : s.title}</span>
                        {why && <span className="block text-[12px] text-muted2 leading-snug mt-0.5">{why}</span>}
                      </span>
                      <span className="text-[12px] text-muted2 whitespace-nowrap">{shortDate(s.release_date)}</span>
                      <span className="flex items-center gap-1.5 text-[12px] font-semibold min-w-0">
                        <span className={cn("w-2 h-2 rounded-full shrink-0", s.partial ? "" : STATUS_DOT[s.status] || "bg-muted2")} style={s.partial ? HALF : undefined} />
                        <span className="truncate">{s.status}</span>
                      </span>
                    </button>
                  );
                })}
              </div>
              <div className="overflow-x-auto max-sm:hidden">
                <table className="w-full">
                  <thead>
                    <tr className="bg-surface/70 border-b border-line">
                      {["Song", "Artist", "Release date", "Status"].map((h) => (
                        <th key={h} className="t-head text-left px-4 py-2.5 font-medium">
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {songs.map((s) => (
                      <tr key={s.track_id} className={cn("t-row cursor-pointer", s.track_id === openId && "bg-brand-light/50")} onClick={() => setOpenId(s.track_id)}>
                        <td className="t-cell font-semibold">
                          <Hl text={s.title} q={q.trim()} />
                          {reason(s) && !songArtist(s).toLowerCase().includes(n) && <div className="text-[12px] text-muted2 font-normal">{reason(s)}</div>}
                        </td>
                        <td className="t-cell text-[13px] text-muted2">{songArtist(s) ? <Hl text={songArtist(s)} q={q.trim()} /> : "—"}</td>
                        <td className="t-cell text-[13px] whitespace-nowrap">{s.release_date ? formatDate(s.release_date) : "—"}</td>
                        <td className="t-cell">
                          <SongStatus status={s.status} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {!songs.length && (
                <p className="px-4 py-6 text-[13px] text-muted2">
                  No songs match.{" "}
                  <button
                    type="button"
                    className="font-semibold text-brand"
                    onClick={() => {
                      setQ("");
                      setParam("status", "");
                    }}
                  >
                    Show all
                  </button>
                </p>
              )}
            </div>
            <p className="text-[12.5px] text-muted2 mt-3">Tap a song for its details, writers and splits. The status is set by your publisher; "Needs update" shows when something on the track has to be fixed before it can be registered.</p>
            <SongSheet s={open} onClose={closeSong} publisher={data.publisher} />
          </>
        )}
      </div>
    </Layout>
  );
}

// ------------------------------------------------------------------ royalties
export function MyRoyalties() {
  const [service, setService] = useState("");
  const [song, setSong] = useState("");
  const data = useLoad("my-royalties", "/my/royalties", service || song ? { service, song } : undefined);
  const [lastGood, setLastGood] = useState(data);
  useEffect(() => {
    if (data) setLastGood(data);
  }, [data]);
  const d = data || lastGood;
  return (
    <Layout title="Royalties" subtitle={d?.before_fee ? "What your songs earned, before the publisher's percentage" : "What your songs earned, by service and month"}>
      <div className="min-h-[70vh]">
        {!d ? (
          <LoadingPanel rows={6} />
        ) : !d.songs_with_income ? (
          <div className="panel">
            <EmptyState icon={Disc3} title="No royalties yet" body="Your earnings appear here once a statement with your songs comes in." />
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
              <StatCard label={d.label} value={money(d.total)} sub={service || song ? "With these filters" : "From every statement so far"} icon={Wallet} />
              <StatCard label="Songs earning" value={d.songs_earning} sub={`Of your ${d.songs_with_income} with income`} icon={ListMusic} />
              <StatCard label="Biggest service" value={d.by_service[0] ? serviceName(d.by_service[0].name) : "—"} sub={d.by_service[0] ? money(d.by_service[0].amount) : ""} icon={Disc3} />
            </div>
            <div className="flex items-center gap-2 flex-wrap mb-4">
              <FilterSelect value={service} onChange={setService} allLabel="All services" icon={Disc3} options={d.options.services.map((s) => ({ value: s, label: serviceName(s) }))} />
              <FilterSelect value={song} onChange={setSong} allLabel="All my songs" icon={ListMusic} options={d.options.songs.map((s) => ({ value: s.track_id, label: s.title }))} />
              {(service || song) && (
                <button
                  onClick={() => {
                    setService("");
                    setSong("");
                  }}
                  className="text-[12.5px] text-muted2 hover:text-ink px-2"
                >
                  Clear filters
                </button>
              )}
            </div>
            <div className="mb-4">
              <MySongEarnings songs={d.songs || []} label={d.label} total={d.total} />
            </div>
            <div className="grid lg:grid-cols-2 gap-4 mb-4">
              <section className="panel p-5">
                <div className="eyebrow mb-3">By service</div>
                <Bars items={d.by_service.map((s) => ({ key: s.name, label: serviceName(s.name), amount: s.amount }))} activeKey={service} onPick={(i) => setService(service === i.key ? "" : i.key)} />
              </section>
              <section className="panel p-5">
                <div className="eyebrow mb-3">By when the plays happened</div>
                <Bars labelWidth={90} items={d.by_period.slice(0, 12).map((p) => ({ key: p.period, label: monthLabel(p.period), amount: p.amount }))} />
              </section>
            </div>
          </>
        )}
      </div>
    </Layout>
  );
}

/** Phones get a sheet from the bottom; wider screens one from the right. */
function useNarrow() {
  const q = "(max-width: 767px)";
  const [narrow, setNarrow] = useState(() => typeof window !== "undefined" && window.matchMedia(q).matches);
  useEffect(() => {
    const m = window.matchMedia(q);
    const fn = () => setNarrow(m.matches);
    m.addEventListener?.("change", fn);
    return () => m.removeEventListener?.("change", fn);
  }, []);
  return narrow;
}

/** One row per song with its total; tapping one slides up its breakdown by service or by month. */
function MySongEarnings({ songs, label, total }) {
  const [openId, setOpenId] = useState(null);
  const [by, setBy] = useState("month");
  const narrow = useNarrow();
  const g = songs.find((x) => x.track_id === openId);
  const rows = g ? (by === "service" ? g.services.map((x) => [serviceName(x.name), x.amount]) : g.months.map((x) => [`${monthLabel(x.period)} · ${serviceName(x.service)}`, x.amount])) : [];
  return (
    <div className="panel overflow-hidden">
      <div className="px-4 py-3 border-b border-line flex items-center justify-between gap-3 flex-wrap">
        <span className="eyebrow">Your songs</span>
        <span className="text-[12px] text-muted2">Tap a song for the breakdown</span>
      </div>
      {songs.map((x) => (
        <button
          key={x.track_id}
          type="button"
          onClick={() => setOpenId(x.track_id)}
          className={cn("w-full flex items-center gap-3 px-4 py-3 text-left border-b border-line last:border-b-0", openId === x.track_id ? "bg-brand-light" : "hover:bg-surface/60")}
        >
          <span className="flex-1 min-w-0">
            <span className="block text-[14px] font-semibold truncate">{x.title}</span>
            <span className="block text-[12px] text-muted2 truncate">
              {x.artist ? `${x.artist} · ` : ""}
              {x.my_share != null ? `your split ${Number(x.my_share)}%` : ""}
            </span>
          </span>
          <span className="text-[13px] font-semibold">
            <Money value={x.total} />
          </span>
          <ChevronRight className="w-4 h-4 text-muted2 shrink-0" />
        </button>
      ))}
      <div className="px-4 py-3 border-t border-line flex justify-between gap-3 text-[12.5px] flex-wrap">
        <span className="text-muted2">
          {songs.length} song{songs.length === 1 ? "" : "s"} · only your part of each song is shown
        </span>
        <span>
          {label} <b className="font-mono">{money(total)}</b>
        </span>
      </div>

      <Sheet open={!!g} onOpenChange={(v) => !v && setOpenId(null)}>
        <SheetContent side={narrow ? "bottom" : "right"} className={cn("p-0 flex flex-col", narrow ? "max-h-[82vh] rounded-t-2xl" : "w-full sm:max-w-md")}>
          {g && (
            <>
              {narrow && <div className="mx-auto mt-2.5 h-1 w-10 rounded-full bg-line shrink-0" aria-hidden="true" />}
              <div className="px-5 pt-4 pb-3 border-b border-line pr-12">
                <SheetTitle className="font-display text-[22px] font-semibold leading-tight">{g.title}</SheetTitle>
                <SheetDescription className="text-[12.5px] text-muted2">
                  {g.artist ? `${g.artist} · ` : ""}
                  {g.my_share != null ? `your split ${Number(g.my_share)}%` : ""}
                </SheetDescription>
                <div className="font-display text-[28px] font-semibold mt-2">{money(g.total)}</div>
                <div className="text-[12px] text-muted2">{label} from this song</div>
              </div>
              <div className="px-5 py-4 overflow-y-auto flex-1">
                <div className="inline-flex gap-0.5 p-[3px] rounded-lg border border-line bg-surface mb-3" role="group" aria-label="Break down by">
                  {[
                    ["month", "By month"],
                    ["service", "By service"],
                  ].map(([k, l]) => (
                    <button key={k} type="button" aria-pressed={by === k} onClick={() => setBy(k)} className={cn("h-8 px-3.5 rounded-md text-[12.5px] font-semibold", by === k ? "bg-card text-ink shadow-sm" : "text-muted2")}>
                      {l}
                    </button>
                  ))}
                </div>
                <div className="rounded-lg overflow-hidden border border-line">
                  {rows.map(([k, v], i) => (
                    <div key={i} className="flex justify-between gap-3 px-3 py-2.5 text-[13px] odd:bg-surface/70">
                      <span className="text-muted2">{k}</span>
                      <Money value={v} />
                    </div>
                  ))}
                  <div className="flex justify-between gap-3 px-3 py-2.5 text-[13px] border-t border-line font-semibold">
                    <span>{g.title} total</span>
                    <Money value={g.total} />
                  </div>
                </div>
              </div>
              <div className="px-5 py-3 border-t border-line">
                <Button className="w-full bg-brand hover:bg-brand-hover" onClick={() => setOpenId(null)}>
                  Done
                </Button>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}

// ------------------------------------------------------------------ search
export function WriterSearch() {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState([]);
  const navigate = useNavigate();
  useEffect(() => {
    if (q.trim().length < 2) {
      setRows([]);
      return undefined;
    }
    const t = setTimeout(() => {
      api
        .get("/my/songs", { params: { q } })
        .then(({ data }) => setRows(data.songs.slice(0, 8)))
        .catch(() => {});
    }, 180);
    return () => clearTimeout(t);
  }, [q]);
  const go = (id) => {
    setOpen(false);
    setQ("");
    navigate(`/my-songs?open=${id}`);
  };
  return (
    <Popover open={open && q.trim().length >= 2} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <label className="hidden md:flex items-center gap-2 h-9 w-[280px] xl:w-[320px] px-3 rounded-lg border border-line bg-card text-muted2 focus-within:border-brand-ring">
          <Search className="w-4 h-4 shrink-0" />
          <input
            aria-label="Search your songs"
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setOpen(true);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" && rows[0]) go(rows[0].track_id);
            }}
            placeholder="Search your songs…"
            className="bg-transparent outline-none text-[13px] text-ink w-full placeholder:text-muted2"
          />
        </label>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[360px] p-1" onOpenAutoFocus={(e) => e.preventDefault()}>
        {!rows.length && <div className="px-3 py-4 text-[13px] text-muted2">None of your songs match "{q}".</div>}
        {rows.map((s) => (
          <button key={s.track_id} onClick={() => go(s.track_id)} className="w-full text-left px-3 py-2 rounded-lg hover:bg-brand-light/60 flex items-center justify-between gap-3">
            <span className="min-w-0">
              <span className="block text-[13px] font-medium text-ink truncate">{s.title}</span>
              <span className="block text-[11.5px] text-muted2 truncate">{s.artist}</span>
            </span>
            <SongStatus status={s.status} />
          </button>
        ))}
      </PopoverContent>
    </Popover>
  );
}

export { StatusPill };
